import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  CalendarIcon,
  UserIcon,
  ClockIcon,
  EyeIcon,
  ShareIcon,
  BookmarkIcon,
  ArrowLeftIcon,
  ThumbsUpIcon,
  MessageCircleIcon,
  BookOpenIcon
} from "lucide-react";
import { contentService } from "../../services";

const BlogPost = () => {
  const { postId } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch blog post from API
  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        const postData = await contentService.getBlogPostById(postId);
        setBlogPost(postData);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post');
        // Fallback to mock data
        setBlogPost({
    id: postId,
    title: "Complete Guide to Laparoscopic Surgery: Benefits, Risks, and Recovery",
    subtitle: "Everything you need to know about minimally invasive surgical procedures",
    content: `
      <div class="prose prose-lg max-w-none">
        <p class="lead text-xl text-gray-600 mb-8">
          Laparoscopic surgery, also known as minimally invasive surgery, has revolutionized the field of medicine. This advanced surgical technique uses small incisions and specialized instruments to perform complex procedures with greater precision and faster recovery times.
        </p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">What is Laparoscopic Surgery?</h2>
        <p class="text-gray-700 leading-relaxed mb-6">
          Laparoscopic surgery is a modern surgical technique that allows surgeons to access the inside of the abdomen and pelvis without having to make large incisions in the skin. Instead of a 6 to 12-inch incision needed in traditional open surgery, laparoscopic surgery uses several 0.5-1 cm incisions.
        </p>

        <p class="text-gray-700 leading-relaxed mb-6">
          A laparoscope—a small tube with a light source and camera—is inserted through one of the small incisions. The camera sends images to a video monitor in the operating room, giving the surgeon a magnified view of the patient's internal organs.
        </p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Benefits of Laparoscopic Surgery</h2>
        <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          <li><strong>Smaller incisions:</strong> Result in less scarring and better cosmetic results</li>
          <li><strong>Reduced pain:</strong> Less tissue damage means less post-operative pain</li>
          <li><strong>Shorter hospital stay:</strong> Many procedures can be done as outpatient surgery</li>
          <li><strong>Faster recovery:</strong> Patients typically return to normal activities sooner</li>
          <li><strong>Lower infection risk:</strong> Smaller incisions reduce the risk of surgical site infections</li>
          <li><strong>Less blood loss:</strong> Minimally invasive approach results in minimal bleeding</li>
        </ul>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Common Laparoscopic Procedures</h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          Laparoscopic surgery can be used for a wide variety of procedures, including:
        </p>
        <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          <li>Gallbladder removal (Cholecystectomy)</li>
          <li>Appendix removal (Appendectomy)</li>
          <li>Hernia repair</li>
          <li>Gastric bypass surgery</li>
          <li>Gynecological procedures</li>
          <li>Colon surgery</li>
          <li>Kidney surgery</li>
        </ul>

        <div class="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
          <h3 class="text-lg font-semibold text-blue-900 mb-2">Expert Tip</h3>
          <p class="text-blue-800">
            "The key to successful laparoscopic surgery lies in the surgeon's experience and the hospital's advanced equipment. At TheLivCure, we ensure both factors are optimized for the best patient outcomes."
          </p>
          <p class="text-sm text-blue-600 mt-2">— Dr. Rajesh Kumar, Senior Laparoscopic Surgeon</p>
        </div>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Recovery and Aftercare</h2>
        <p class="text-gray-700 leading-relaxed mb-6">
          Recovery from laparoscopic surgery is typically much faster than traditional open surgery. Most patients can expect:
        </p>
        <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          <li>Same-day or next-day discharge from the hospital</li>
          <li>Return to light activities within 24-48 hours</li>
          <li>Full recovery within 1-2 weeks (depending on the procedure)</li>
          <li>Minimal scarring that fades over time</li>
        </ul>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">Is Laparoscopic Surgery Right for You?</h2>
        <p class="text-gray-700 leading-relaxed mb-6">
          While laparoscopic surgery offers many advantages, it's not suitable for every patient or every condition. Your surgeon will evaluate factors such as:
        </p>
        <ul class="list-disc pl-6 space-y-2 text-gray-700 mb-6">
          <li>The specific condition being treated</li>
          <li>Your overall health and medical history</li>
          <li>Previous abdominal surgeries</li>
          <li>The complexity of the required procedure</li>
        </ul>

        <div class="bg-green-50 border-l-4 border-green-500 p-6 my-8">
          <h3 class="text-lg font-semibold text-green-900 mb-2">Conclusion</h3>
          <p class="text-green-800">
            Laparoscopic surgery represents a significant advancement in surgical care, offering patients less pain, shorter recovery times, and better cosmetic results. If you're considering surgery, discuss with your doctor whether a laparoscopic approach might be right for your situation.
          </p>
        </div>
      </div>
    `,
    author: "Dr. Rajesh Kumar",
    authorSpecialty: "Senior Laparoscopic Surgeon",
    date: "January 15, 2025",
    readTime: "8 min read",
    category: "Surgery",
    tags: ["Laparoscopic", "Minimally Invasive", "Surgery", "Recovery"],
    views: 15420,
    likes: 342,
    comments: 28,
    image: "blog-laparoscopic"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchBlogPost();
  }, [postId]);

  const relatedPosts = [
    {
      id: 2,
      title: "5 Warning Signs You Shouldn't Ignore About Gallbladder Stones",
      author: "Dr. Priya Sharma",
      date: "January 12, 2025",
      readTime: "5 min read",
      category: "General Health"
    },
    {
      id: 3,
      title: "Understanding PCOD vs PCOS: Symptoms, Causes, and Treatment",
      author: "Dr. Sunita Patel", 
      date: "January 10, 2025",
      readTime: "7 min read",
      category: "Women's Health"
    },
    {
      id: 4,
      title: "Post-Surgery Care: Essential Tips for Faster Recovery",
      author: "Dr. Amit Singh",
      date: "January 8, 2025", 
      readTime: "6 min read",
      category: "Surgery"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Blog post not found'}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Article Header */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          
          <Badge className="mb-4">{blogPost.category}</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {blogPost.title}
          </h1>
          <p className="text-xl text-gray-600 mb-8">{blogPost.subtitle}</p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">{blogPost.author}</div>
                <div className="text-sm text-gray-600">{blogPost.authorSpecialty}</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <CalendarIcon className="w-4 h-4" />
                <span>{blogPost.date}</span>
              </div>
              <div className="flex items-center space-x-1">
                <ClockIcon className="w-4 h-4" />
                <span>{blogPost.readTime}</span>
              </div>
              <div className="flex items-center space-x-1">
                <EyeIcon className="w-4 h-4" />
                <span>{blogPost.views.toLocaleString()} views</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <article className="lg:col-span-3">
              <Card>
                <CardContent className="p-8">
                  {/* Article Image */}
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center mb-8">
                    <div className="text-center">
                      <BookOpenIcon className="w-16 h-16 text-blue-600 mx-auto mb-2" />
                      <p className="text-gray-600">Article Featured Image</p>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div 
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: blogPost.content }}
                  />

                  {/* Tags */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {blogPost.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Social Actions */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Button variant="outline" size="sm">
                          <ThumbsUpIcon className="w-4 h-4 mr-2" />
                          {blogPost.likes} Likes
                        </Button>
                        <Button variant="outline" size="sm">
                          <MessageCircleIcon className="w-4 h-4 mr-2" />
                          {blogPost.comments} Comments
                        </Button>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                          <ShareIcon className="w-4 h-4 mr-2" />
                          Share
                        </Button>
                        <Button variant="outline" size="sm">
                          <BookmarkIcon className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Author Info */}
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <UserIcon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{blogPost.author}</h3>
                    <p className="text-sm text-gray-600 mb-4">{blogPost.authorSpecialty}</p>
                    <Button variant="outline" size="sm" className="w-full">
                      View Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Related Articles */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedPosts.map((post) => (
                      <Link key={post.id} to={`/blog/${post.id}`}>
                        <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <h4 className="font-medium text-gray-900 mb-1 text-sm line-clamp-2">
                            {post.title}
                          </h4>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{post.author}</span>
                            <span>{post.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter */}
              <Card className="bg-blue-50">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Stay Updated</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Get the latest health articles delivered to your inbox
                  </p>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Subscribe to Newsletter
                  </Button>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </section>

      {/* Comments Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Comments ({blogPost.comments})
              </h2>
              
              {/* Sample Comments */}
              <div className="space-y-6">
                {[1, 2, 3].map((comment) => (
                  <div key={comment} className="border-b border-gray-100 pb-6 last:border-b-0">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-semibold text-gray-900">Patient #{comment}</span>
                          <span className="text-sm text-gray-500">2 days ago</span>
                        </div>
                        <p className="text-gray-600 text-sm">
                          This article was very helpful! I was considering laparoscopic surgery and this gave me all the information I needed to make an informed decision.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Load More Comments
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPost;